import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { invoiceService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import CreateInvoiceModal from '../components/invoices/CreateInvoiceModal';
import SendInvoiceModal from '../components/invoices/SendInvoiceModal';
import AddPaymentModal from '../components/invoices/AddPaymentModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  FileText,
  Plus,
  DollarSign,
  Send,
  Eye,
  AlertTriangle,
  CheckCircle,
  Clock,
  Ban,
  ShieldCheck,
  Landmark,
  ChevronDown,
  Download,
  RotateCcw
} from 'lucide-react';

const Invoices = () => {
  const { user } = useAuth();
  const canApprove = hasPermission(user, 'approve_invoice');
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const urlCaseId = searchParams.get('case_id');
  const urlClientId = searchParams.get('client');
  const [showCreateModal, setShowCreateModal] = useState(!!urlCaseId);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('draft');
  const [filters, setFilters] = useState({
    status: 'draft',
    case_id: '',
    client_id: urlClientId || '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [sendModalInvoice, setSendModalInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchMessage, setBatchMessage] = useState(null);
  const [showBatchMenu, setShowBatchMenu] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = (index, e) => {
    // Don't interfere with link/button clicks
    if (e.target.closest('a') || e.target.closest('button') || e.target.type === 'checkbox') return;

    const id = invoices[index].id;

    if (e.shiftKey && lastClickedIndex !== null) {
      // Shift+click: select range
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          next.add(invoices[i].id);
        }
        return next;
      });
    } else if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+click: toggle individual
      toggleSelect(id);
    } else {
      // Plain click: select only this one
      setSelectedIds(new Set([id]));
    }
    setLastClickedIndex(index);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(invoices.map(i => i.id)));
    }
  };

  const selectedInvoices = invoices.filter(i => selectedIds.has(i.id));
  const selectedDrafts = selectedInvoices.filter(i => i.status === 'draft');
  const selectedPendingApproval = selectedInvoices.filter(i => i.status === 'pending_approval');
  const selectedApproved = selectedInvoices.filter(i => i.status === 'approved');
  const selectedVoidable = selectedInvoices.filter(i =>
    ['pending_approval', 'approved', 'sent', 'partial', 'overdue'].includes(i.status)
  );
  const hasBatchActions = selectedDrafts.length > 0 || selectedApproved.length > 0
    || selectedPendingApproval.length > 0 || selectedVoidable.length > 0;

  const runBatchAction = async (action, label) => {
    setBatchProcessing(true);
    setBatchMessage(null);
    try {
      const response = await action();
      const result = response.data;
      setBatchMessage({
        variant: result.failed?.length ? 'warning' : 'success',
        text: result.message
      });
      setSelectedIds(new Set());
      fetchInvoices();
      fetchStats();
    } catch (error) {
      setBatchMessage({ variant: 'error', text: `Failed to ${label}` });
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleBatchSubmitForApproval = () => {
    const ids = selectedDrafts.map(i => i.id);
    runBatchAction(() => invoiceService.batchSubmitForApproval(ids), 'submit for approval');
  };

  const handleBatchApplyTrust = () => {
    const ids = selectedApproved.map(i => i.id);
    runBatchAction(() => invoiceService.batchApplyTrust(ids), 'apply trust');
  };

  const handleBatchSend = () => {
    const ids = selectedApproved.map(i => i.id);
    runBatchAction(() => invoiceService.batchSendInvoices(ids), 'send invoices');
  };

  const handleBatchApproveAndApplyTrust = async () => {
    setBatchProcessing(true);
    setBatchMessage(null);
    try {
      // First approve the selected drafts/pending
      const toApprove = selectedInvoices.filter(i => i.status === 'draft' || i.status === 'pending_approval');
      if (toApprove.length > 0) {
        await invoiceService.batchApproveInvoices(toApprove.map(i => i.id));
      }
      // Then apply trust to all (now approved)
      const allIds = selectedInvoices.map(i => i.id);
      const trustResult = await invoiceService.batchApplyTrust(allIds);
      const result = trustResult.data;
      setBatchMessage({
        variant: result.failed?.length ? 'warning' : 'success',
        text: result.message
      });
      setSelectedIds(new Set());
      fetchInvoices();
      fetchStats();
    } catch (error) {
      setBatchMessage({ variant: 'error', text: 'Failed to approve and apply trust' });
    } finally {
      setBatchProcessing(false);
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        ...pagination,
        // Remove empty filters
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      
      const response = await invoiceService.getInvoices(params);
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await invoiceService.getInvoiceStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
    }
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    fetchInvoices();
    fetchStats();
  }, [fetchInvoices, fetchStats]);

  const handleSendInvoice = async (email) => {
    if (!sendModalInvoice) return;
    try {
      await invoiceService.sendInvoice(sendModalInvoice.id, email);
      fetchInvoices(); // Refresh list
      setSendModalInvoice(null);
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error; // Re-throw so modal can show error
    }
  };

  const openSendModal = (invoice) => {
    setSendModalInvoice(invoice);
  };

  const handleVoidInvoice = async (invoiceId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Void Invoice',
      message: 'Are you sure you want to void this invoice? This action cannot be undone.',
      confirmText: 'Void Invoice',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await invoiceService.voidInvoice(invoiceId);
          fetchInvoices();
        } catch (error) {
          console.error('Error voiding invoice:', error);
        }
      }
    });
  };

  const handleRejectInvoice = async (invoiceId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Return to Draft',
      message: 'Are you sure you want to return this invoice to draft? It will need to be re-submitted for approval.',
      confirmText: 'Return to Draft',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await invoiceService.rejectInvoice(invoiceId);
          fetchInvoices();
        } catch (error) {
          console.error('Error returning invoice to draft:', error);
        }
      }
    });
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await invoiceService.downloadInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const caseTitle = invoice.case_title ? `_${invoice.case_title.replace(/[^a-zA-Z0-9 _-]/g, '').trim()}` : '';
      link.setAttribute('download', `Invoice_${invoice.invoice_number || invoice.id}${caseTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    const statusMap = {
      draft: 'draft',
      pending_approval: 'pending_approval',
      unpaid: 'approved,sent,partial,overdue',
      paid: 'paid',
      all: '',
      archive: 'void',
    };
    setFilters(prev => ({ ...prev, status: statusMap[tab] || '' }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'pending_approval': return <Clock className="w-4 h-4" />;
      case 'approved': return <ShieldCheck className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'partial': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      case 'void': return <Ban className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="w-8 h-8 mr-3 text-yellow-600" />
              Invoices
            </h1>
            <p className="text-gray-600 mt-1">Manage billing and payments</p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-yellow-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Outstanding</p>
                  <p className="text-xl font-bold text-yellow-600">
                    {invoiceService.formatCurrency(stats.total_outstanding)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Overdue</p>
                  <p className="text-xl font-bold text-red-600">
                    {invoiceService.formatCurrency(stats.overdue_amount)}
                  </p>
                  <p className="text-xs text-gray-500">{stats.overdue_count} invoices</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Paid This Month</p>
                  <p className="text-xl font-bold text-green-600">
                    {invoiceService.formatCurrency(stats.paid_amount)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-gray-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Invoices</p>
                  <p className="text-xl font-bold text-gray-600">{stats.total_invoices}</p>
                  <p className="text-xs text-gray-500">{stats.draft_count} drafts</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Tabs */}
        <div className="border-t border-gray-200 -mx-6 -mb-6 px-6">
          <div className="flex space-x-0 overflow-x-auto">
            {[
              { id: 'draft', label: 'Draft' },
              { id: 'pending_approval', label: 'Pending Approval' },
              { id: 'unpaid', label: 'Unpaid' },
              { id: 'paid', label: 'Paid' },
              { id: 'all', label: 'All' },
              { id: 'archive', label: 'Archive' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Batch Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-yellow-800">
              {selectedIds.size} invoice{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-yellow-600 hover:text-yellow-800 underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center space-x-3">
            {batchProcessing && (
              <span className="text-sm text-gray-500">Processing...</span>
            )}
            <div className="relative">
              <button
                onClick={() => setShowBatchMenu(!showBatchMenu)}
                onBlur={() => setTimeout(() => setShowBatchMenu(false), 200)}
                disabled={batchProcessing}
                className="bg-yellow-500 text-white px-4 py-1.5 rounded-lg hover:bg-yellow-600 flex items-center text-sm disabled:opacity-50"
              >
                Actions
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              {showBatchMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* Draft actions */}
                  {selectedDrafts.length > 0 && (
                    <button
                      onClick={() => {
                        setShowBatchMenu(false);
                        setConfirmDialog({
                          isOpen: true,
                          title: 'Send for Approval',
                          message: `Submit ${selectedDrafts.length} draft invoice${selectedDrafts.length > 1 ? 's' : ''} for approval?`,
                          onConfirm: handleBatchSubmitForApproval,
                          variant: 'warning',
                          confirmText: 'Submit All'
                        });
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Send className="w-4 h-4 mr-3 text-yellow-500" />
                      Send for Approval ({selectedDrafts.length})
                    </button>
                  )}

                  {/* Approve — works on drafts + pending */}
                  {canApprove && (selectedDrafts.length > 0 || selectedPendingApproval.length > 0) && (
                    <>
                      <button
                        onClick={() => {
                          setShowBatchMenu(false);
                          const toApprove = selectedInvoices.filter(i => i.status === 'draft' || i.status === 'pending_approval');
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Approve Invoices',
                            message: `Approve ${toApprove.length} invoice${toApprove.length > 1 ? 's' : ''}?`,
                            onConfirm: () => runBatchAction(() => invoiceService.batchApproveInvoices(toApprove.map(i => i.id)), 'approve invoices'),
                            variant: 'warning',
                            confirmText: 'Approve All'
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <ShieldCheck className="w-4 h-4 mr-3 text-green-500" />
                        Approve ({selectedDrafts.length + selectedPendingApproval.length})
                      </button>
                      <button
                        onClick={() => {
                          setShowBatchMenu(false);
                          const toProcess = selectedInvoices.filter(i => i.status === 'draft' || i.status === 'pending_approval');
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Approve and Apply Trust',
                            message: `Approve ${toProcess.length} invoice${toProcess.length > 1 ? 's' : ''} and apply trust funds?`,
                            onConfirm: handleBatchApproveAndApplyTrust,
                            variant: 'warning',
                            confirmText: 'Approve & Apply Trust'
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <Landmark className="w-4 h-4 mr-3 text-blue-500" />
                        Approve and Apply Trust
                      </button>
                    </>
                  )}

                  {/* Approved actions */}
                  {selectedApproved.length > 0 && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => {
                          setShowBatchMenu(false);
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Apply Trust',
                            message: `Apply trust funds to ${selectedApproved.length} approved invoice${selectedApproved.length > 1 ? 's' : ''}?`,
                            onConfirm: handleBatchApplyTrust,
                            variant: 'warning',
                            confirmText: 'Apply Trust'
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <Landmark className="w-4 h-4 mr-3 text-blue-500" />
                        Apply Trust ({selectedApproved.length})
                      </button>
                      <button
                        onClick={() => {
                          setShowBatchMenu(false);
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Send Invoices',
                            message: `Send ${selectedApproved.length} approved invoice${selectedApproved.length > 1 ? 's' : ''} to clients?`,
                            onConfirm: handleBatchSend,
                            variant: 'warning',
                            confirmText: 'Send All'
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <Send className="w-4 h-4 mr-3 text-yellow-500" />
                        Send ({selectedApproved.length})
                      </button>
                    </>
                  )}

                  {/* Return to draft — pending approval */}
                  {selectedPendingApproval.length > 0 && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => {
                          setShowBatchMenu(false);
                          const ids = selectedPendingApproval.map(i => i.id);
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Return to Draft',
                            message: `Return ${ids.length} invoice${ids.length > 1 ? 's' : ''} to draft? They will need to be re-submitted for approval.`,
                            onConfirm: () => runBatchAction(() => invoiceService.batchReturnToDraft(ids), 'return invoices to draft'),
                            variant: 'warning',
                            confirmText: 'Return to Draft'
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <RotateCcw className="w-4 h-4 mr-3 text-orange-500" />
                        Return to Draft ({selectedPendingApproval.length})
                      </button>
                    </>
                  )}

                  {/* Void — anything issued and not yet paid */}
                  {selectedVoidable.length > 0 && (
                    <button
                      onClick={() => {
                        setShowBatchMenu(false);
                        const ids = selectedVoidable.map(i => i.id);
                        setConfirmDialog({
                          isOpen: true,
                          title: 'Void Invoices',
                          message: `Void ${ids.length} invoice${ids.length > 1 ? 's' : ''}? This action cannot be undone.`,
                          onConfirm: () => runBatchAction(() => invoiceService.batchVoidInvoices(ids), 'void invoices'),
                          variant: 'danger',
                          confirmText: 'Void All'
                        });
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <Ban className="w-4 h-4 mr-3" />
                      Void ({selectedVoidable.length})
                    </button>
                  )}

                  {!hasBatchActions && (
                    <p className="px-4 py-2 text-sm text-gray-500">No actions available for the selected invoices</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch status message */}
      {batchMessage && (
        <div className={`rounded-lg px-4 py-3 text-sm ${
          batchMessage.variant === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          batchMessage.variant === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
          'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {batchMessage.text}
          <button onClick={() => setBatchMessage(null)} className="ml-3 underline text-xs">Dismiss</button>
        </div>
      )}

      {/* Invoice List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {invoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No invoices found</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="flex items-center gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="w-8 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={invoices.length > 0 && selectedIds.size === invoices.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 text-yellow-600 rounded border-gray-300"
                  />
                </div>
                <div className="grid grid-cols-12 gap-4 flex-1">
                  <div className="col-span-2">Invoice #</div>
                  <div className="col-span-2">Client</div>
                  <div className="col-span-2">Case</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Date</div>
                  <div className="col-span-1">Due</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-1">Actions</div>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
              {(invoices || []).map((invoice, index) => (
                <div
                  key={invoice.id}
                  className={`px-6 py-4 cursor-pointer select-none hover:bg-gray-50 ${selectedIds.has(invoice.id) ? 'bg-yellow-50' : ''}`}
                  onClick={(e) => handleRowClick(index, e)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(invoice.id)}
                        onChange={() => toggleSelect(invoice.id)}
                        className="h-4 w-4 text-yellow-600 rounded border-gray-300"
                      />
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-center flex-1">
                    {/* Invoice Number */}
                    <div className="col-span-2">
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="text-yellow-600 hover:text-yellow-700 font-medium"
                      >
                        {invoice.invoice_number}
                      </Link>
                    </div>

                    {/* Client */}
                    <div className="col-span-2">
                      <p className="text-sm text-gray-900">{invoice.client_name}</p>
                    </div>

                    {/* Case */}
                    <div className="col-span-2">
                      <p className="text-sm text-gray-900 truncate">{invoice.case_title}</p>
                    </div>

                    {/* Status */}
                    <div className="col-span-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${invoiceService.getStatusColor(invoice.status)}`}>
                        <span className="mr-1">{getStatusIcon(invoice.status)}</span>
                        {invoiceService.getStatusLabel(invoice.status)}
                      </span>
                      {invoice.is_overdue && (
                        <div className="text-xs text-red-600 mt-1">
                          {invoiceService.getDaysOverdue(invoice)} days overdue
                        </div>
                      )}
                    </div>

                    {/* Invoice Date */}
                    <div className="col-span-1">
                      <p className="text-sm text-gray-600">
                        {invoiceService.formatDate(invoice.invoice_date)}
                      </p>
                    </div>

                    {/* Due Date */}
                    <div className="col-span-1">
                      <p className={`text-sm ${invoice.is_overdue ? 'text-red-600' : 'text-gray-600'}`}>
                        {invoiceService.formatDate(invoice.due_date)}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="col-span-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {invoiceService.formatCurrency(invoice.total_amount)}
                        </p>
                        {parseFloat(invoice.paid_amount) > 0 && (
                          <p className="text-xs text-green-600">
                            Paid: {invoiceService.formatCurrency(invoice.paid_amount)}
                          </p>
                        )}
                        {invoiceService.calculateBalance(invoice) > 0 && (
                          <p className="text-xs text-gray-500">
                            Balance: {invoiceService.formatCurrency(invoiceService.calculateBalance(invoice))}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1">
                      <div className="flex space-x-2">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="text-gray-400 hover:text-gray-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={() => handleDownloadPDF(invoice)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>

                        {invoice.status === 'draft' && (
                          <button
                            onClick={async () => {
                              try {
                                await invoiceService.submitForApproval(invoice.id);
                                fetchInvoices();
                              } catch (e) { /* handled by batch message */ }
                            }}
                            className="text-yellow-500 hover:text-yellow-700"
                            title="Send for Approval"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}

                        {invoice.status === 'pending_approval' && (
                          <>
                            {canApprove && (
                              <button
                                onClick={async () => {
                                  try {
                                    await invoiceService.approveInvoice(invoice.id);
                                    fetchInvoices();
                                  } catch (e) { /* handled by batch message */ }
                                }}
                                className="text-green-500 hover:text-green-700"
                                title="Approve"
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleRejectInvoice(invoice.id)}
                              className="text-orange-400 hover:text-orange-600"
                              title="Return to Draft"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleVoidInvoice(invoice.id)}
                              className="text-red-400 hover:text-red-600"
                              title="Void Invoice"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {invoice.status === 'approved' && (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  await invoiceService.applyTrust(invoice.id);
                                  fetchInvoices();
                                } catch (e) { /* handled by batch message */ }
                              }}
                              className="text-blue-500 hover:text-blue-700"
                              title="Apply Trust"
                            >
                              <Landmark className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openSendModal(invoice)}
                              className="text-yellow-500 hover:text-yellow-700"
                              title="Send Invoice"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {(invoice.status === 'sent' || invoice.status === 'partial') && (
                          <>
                            <button
                              onClick={() => setPaymentInvoice(invoice)}
                              className="text-green-400 hover:text-green-600"
                              title="Record Payment"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleVoidInvoice(invoice.id)}
                              className="text-red-400 hover:text-red-600"
                              title="Void Invoice"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {invoices.length === pagination.limit && (
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                    disabled={pagination.skip === 0}
                    className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Showing {pagination.skip + 1} - {pagination.skip + invoices.length}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          initialCaseId={urlCaseId || undefined}
          onClose={() => {
            setShowCreateModal(false);
            if (urlCaseId) setSearchParams({});
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            if (urlCaseId) setSearchParams({});
            setActiveTab('draft');
            setFilters(prev => ({ ...prev, status: 'draft' }));
            fetchInvoices();
            fetchStats();
            setBatchMessage({ variant: 'success', text: 'Invoice created successfully and saved as draft.' });
          }}
        />
      )}

      {/* Send Invoice Modal */}
      {sendModalInvoice && (
        <SendInvoiceModal
          invoice={sendModalInvoice}
          onClose={() => setSendModalInvoice(null)}
          onSuccess={handleSendInvoice}
        />
      )}

      {/* Record Payment Modal */}
      {paymentInvoice && (
        <AddPaymentModal
          invoice={paymentInvoice}
          onClose={() => setPaymentInvoice(null)}
          onSuccess={() => {
            setPaymentInvoice(null);
            fetchInvoices();
            fetchStats();
          }}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant || 'danger'}
        confirmText={confirmDialog.confirmText || 'Confirm'}
      />
    </div>
  );
};

export default Invoices;